import type { AiOperationTargetRef } from '../../../types/aiOperational'
import type {
  AutomationAction,
  AutomationTrigger,
} from '../../../types/automation'

export type CompiledAutomationDraft = {
  name: string
  description: string
  triggers: AutomationTrigger[]
  then: AutomationAction[]
  warnings: string[]
}

export type CompiledAutomationActionBundle = {
  name: string
  description: string
  actions: AutomationAction[]
  warnings: string[]
}

export type CompileAutomationDraftInput = {
  prompt: string
  selectedTargets: AiOperationTargetRef[]
  actorId: string
}

const newActionId = () => `act_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const titleCase = (value: string) => value.replace(/\b\w/g, (letter) => letter.toUpperCase())

const trimmedSentence = (value: string, max = 72) => {
  const normalized = value.trim().replace(/\s+/g, ' ')
  if (!normalized) return 'Untitled automation'
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trimEnd()}…`
}

const triggerKindForTarget = (target: AiOperationTargetRef | undefined): 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST' | 'ANY' => {
  if (!target) return 'ANY'
  if (target.type === 'GOAL') return 'GOAL'
  if (target.type === 'TASK') return 'TASK'
  if (target.type === 'SUBTASK') return 'SUBTASK'
  if (target.type === 'CHECKLIST') return 'CHECKLIST'
  return 'ANY'
}

const parseScheduleTrigger = (prompt: string): { trigger: AutomationTrigger; warning?: string } | null => {
  const lower = prompt.toLowerCase()
  if (/\bevery weekday\b|\beach weekday\b/.test(lower)) {
    return { trigger: { type: 'DATE_REACHED', config: { cron: '0 9 * * 1-5' } } }
  }
  if (/\bevery week\b|\bweekly\b|\beach week\b/.test(lower)) {
    return { trigger: { type: 'DATE_REACHED', config: { cron: '0 9 * * 1' } } }
  }
  if (/\bevery day\b|\bdaily\b|\beach day\b/.test(lower)) {
    return { trigger: { type: 'DATE_REACHED', config: { cron: '0 9 * * *' } } }
  }
  return null
}

const promptSoundsLikeFutureAutomation = (prompt: string) => {
  const lower = prompt.toLowerCase()
  return (
    /\bwhen\b|\bwhenever\b|\bevery time\b/.test(lower)
    || /\bevery day\b|\bdaily\b|\beach day\b|\bevery week\b|\bweekly\b|\beach week\b|\bevery weekday\b|\beach weekday\b/.test(lower)
    || /\bafter\b.+\b(completes?|completed|uploads?|submitted|created)\b/.test(lower)
  )
}

const parseTrigger = (prompt: string, selectedTargets: AiOperationTargetRef[]): { trigger: AutomationTrigger; warnings: string[] } => {
  const lower = prompt.toLowerCase()
  const targetKind = triggerKindForTarget(selectedTargets[0])
  const warnings: string[] = []
  const scheduled = parseScheduleTrigger(prompt)
  if (scheduled) {
    if (scheduled.warning) warnings.push(scheduled.warning)
    return { trigger: scheduled.trigger, warnings }
  }
  if (/\bproof\b/.test(lower)) {
    return { trigger: { type: 'PROOF_UPDATED', config: { scope: 'WORKSPACE', nodeKind: 'ANY' } }, warnings }
  }
  if (/\bupload\b/.test(lower)) {
    return { trigger: { type: 'UPLOAD_COMPLETED', config: { providerId: 'ANY' } }, warnings }
  }
  if (/\bform\b/.test(lower) && /\bsubmit|\bsubmitted|\bsubmission\b/.test(lower)) {
    warnings.push('Review the target form before activation. The draft uses a placeholder form id.')
    return { trigger: { type: 'FORM_SUBMITTED', config: { formId: 'REVIEW_REQUIRED' } }, warnings }
  }
  if (/\bchat\b|\bmessage\b|\bmention\b/.test(lower) && /\bwhen\b|\bwhenever\b|\bevery time\b/.test(lower)) {
    return {
      trigger: {
        type: 'CHAT_MESSAGE_POSTED',
        config: { mentionsOnly: /\bmention\b/.test(lower) || /\btag\b/.test(lower) || /\b@/.test(prompt) },
      },
      warnings,
    }
  }
  if (/\bfocus\b/.test(lower)) {
    return { trigger: { type: 'FOCUS_BLOCK_COMPLETED', config: {} }, warnings }
  }
  if (/\bspin\b|\breward\b|\bprize\b/.test(lower)) {
    return { trigger: { type: 'SPIN_WON', config: { tier: 'ANY' } }, warnings }
  }
  if (/\bcomplete\b|\bcompleted\b|\bfinish\b|\bfinished\b|\bdone\b/.test(lower)) {
    return {
      trigger: {
        type: 'NODE_COMPLETED',
        config: { scope: 'WORKSPACE', nodeKind: targetKind },
      },
      warnings,
    }
  }
  if (/\bassign\b|\bassignee\b/.test(lower) && /\bwhen\b|\bwhenever\b|\bevery time\b/.test(lower)) {
    return { trigger: { type: 'TASK_ASSIGNED', config: { scope: 'WORKSPACE', nodeKind: 'ANY' } }, warnings }
  }
  if (/\bchecklist\b/.test(lower) && /\bcreate\b|\bcreated\b|\bnew\b/.test(lower)) {
    return { trigger: { type: 'CHECKLIST_CREATED', config: { scope: 'WORKSPACE', nodeKind: 'ANY' } }, warnings }
  }
  if (/\bcreate\b|\bcreated\b|\bnew\b/.test(lower)) {
    return { trigger: { type: 'NODE_CREATED', config: { scope: 'WORKSPACE', nodeKind: 'ANY' } }, warnings }
  }
  throw new Error('I could not identify the automation trigger yet. Ask for a clear event such as when proof is uploaded, when a task completes, or every day.')
}

const pushAction = (actions: AutomationAction[], action: AutomationAction) => {
  actions.push(action)
}

const createNotifyAction = (prompt: string, actorId: string): AutomationAction => {
  const lower = prompt.toLowerCase()
  const recipientUserId = /\bme\b|\bmy\b|\bmyself\b/.test(lower) ? actorId : undefined
  const recipientRole = recipientUserId ? undefined : 'ADMIN'
  return {
    id: newActionId(),
    type: 'NOTIFY_USER',
    config: {
      title: titleCase(trimmedSentence(prompt, 48)),
      body: 'Review the triggering record and continue the workflow.',
      ...(recipientUserId ? { recipientUserId } : { recipientRole }),
    },
  }
}

const parseChannelId = (prompt: string): string | null => {
  const match = prompt.match(/\b(?:channel|chat)\s+([a-z0-9_-]+)/i)
  if (match?.[1]) return match[1].toLowerCase()
  if (/\bops\b/i.test(prompt)) return 'ops'
  if (/\bgeneral\b/i.test(prompt)) return 'general'
  return null
}

const createSendChatAction = (prompt: string, warnings: string[]): AutomationAction => {
  const channelId = parseChannelId(prompt) ?? 'general'
  if (channelId === 'general' && !/\bgeneral\b/i.test(prompt)) {
    warnings.push('Review the destination channel before activation. The draft defaults to `general`.')
  }
  return {
    id: newActionId(),
    type: 'SEND_CHAT_MESSAGE',
    config: {
      channelId,
      body: 'Automation follow-up triggered. Review the source record for details.',
    },
  }
}

const createLedgerAction = (prompt: string): AutomationAction => ({
  id: newActionId(),
  type: 'LOG_TO_LEDGER',
  config: {
    category: 'AUTOMATION',
    message: trimmedSentence(prompt, 120),
  },
})

const createCompleteAction = (selectedTargets: AiOperationTargetRef[]): AutomationAction => {
  const target = selectedTargets[0]
  if (!target) {
    throw new Error('I need a selected target to create a completion automation. Pick the item first, then ask again.')
  }
  return {
    id: newActionId(),
    type: 'COMPLETE_NODE',
    config: { targetId: target.id },
  }
}

const createAssignAction = (prompt: string, selectedTargets: AiOperationTargetRef[], actorId: string): AutomationAction => {
  const target = selectedTargets[0]
  if (!target) {
    throw new Error('I need a selected target to create an assignment automation. Pick the item first, then ask again.')
  }
  if (!/\bme\b|\bmy\b|\bmyself\b/i.test(prompt)) {
    throw new Error('Phase 2 can create assignment automations for you on the selected item. For other assignees, specify the target later in Automate.')
  }
  return {
    id: newActionId(),
    type: 'ASSIGN_TASK',
    config: { targetId: target.id, assigneeId: actorId },
  }
}

const createStatusAction = (prompt: string, selectedTargets: AiOperationTargetRef[]): AutomationAction | null => {
  const target = selectedTargets[0]
  if (!target) return null
  const lower = prompt.toLowerCase()
  const value = /\bin progress\b/.test(lower)
    ? 'IN_PROGRESS'
    : /\bto do\b|\btodo\b/.test(lower)
      ? 'TODO'
      : /\bcomplete\b|\bcompleted\b|\bdone\b/.test(lower)
        ? 'COMPLETE'
        : null
  if (!value) return null
  return {
    id: newActionId(),
    type: 'UPDATE_FIELD',
    config: {
      targetType: target.type === 'CHECKLIST' ? 'CHECKLIST' : 'NODE',
      targetId: target.id,
      fieldPath: 'boardStatus',
      value,
    },
  }
}

const normalizeCreatedTitle = (raw: string): string => {
  const cleaned = raw
    .trim()
    .replace(/^(called|named|titled)\s+/i, '')
    .replace(/^(to|for)\s+/i, '')
    .replace(/\s+\b(?:when|whenever|after)\b[\s\S]*$/i, '')
    .replace(/[.?!,:;]+$/g, '')
    .trim()
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : ''
}

const extractCreatedTitle = (prompt: string, kind: 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST'): string | null => {
  const quoted = prompt.match(/["“]([^"”]+)["”]/)?.[1]
  if (quoted?.trim()) return normalizeCreatedTitle(quoted)
  const labels = kind === 'SUBTASK'
    ? 'sub(?:-|\\s)?task'
    : kind === 'CHECKLIST'
      ? 'checklist(?:\\s+(?:item|file))?'
      : kind.toLowerCase()
  const match = prompt.match(new RegExp(`\\b(?:create(?:s|d)?|add(?:s|ed)?|make(?:s|d)?|spawn(?:s|ed)?)\\b(?:\\s+(?:a|an|new))?\\s+${labels}\\s+(.+)$`, 'i'))
  return match?.[1] ? normalizeCreatedTitle(match[1]) : null
}

const createNodeCreationAction = (
  prompt: string,
  selectedTargets: AiOperationTargetRef[],
  warnings: string[],
): AutomationAction | null => {
  const lower = prompt.toLowerCase()
  const target = selectedTargets[0]

  if (/\bsub(?:-| )?task\b/.test(lower)) {
    if (!target || target.type !== 'TASK') {
      throw new Error('I need a selected task to create a subtask. Pick the task first, then ask again.')
    }
    const title = extractCreatedTitle(prompt, 'SUBTASK')
    if (!title) throw new Error('Name the subtask in the request, for example "Create a subtask called Confirm venue access."')
    return {
      id: newActionId(),
      type: 'CREATE_NODE',
      config: { title, nodeKind: 'SUBTASK', parentId: target.id, boardKey: target.board },
    }
  }

  if (/\btask\b/.test(lower) && !/\bsub(?:-| )?task\b/.test(lower)) {
    if (!target || target.type !== 'GOAL') {
      throw new Error('I need a selected goal to create a task. Pick the goal first, then ask again.')
    }
    const title = extractCreatedTitle(prompt, 'TASK')
    if (!title) throw new Error('Name the task in the request, for example "Create a task called Lock venue insurance."')
    return {
      id: newActionId(),
      type: 'CREATE_NODE',
      config: { title, nodeKind: 'TASK', parentId: target.id, boardKey: target.board },
    }
  }

  if (/\bgoal\b/.test(lower)) {
    const title = extractCreatedTitle(prompt, 'GOAL')
    if (!title) throw new Error('Name the goal in the request, for example "Create a goal called Launch prep."')
    if (!target?.board) {
      warnings.push('Review the destination board before activation. The draft defaults to `DASHBOARD` for new goals.')
    }
    return {
      id: newActionId(),
      type: 'CREATE_NODE',
      config: { title, nodeKind: 'GOAL', boardKey: target?.board ?? 'DASHBOARD' },
    }
  }

  return null
}

const createChecklistCreationAction = (
  prompt: string,
  selectedTargets: AiOperationTargetRef[],
): AutomationAction | null => {
  if (!/\bchecklist(?:\s+(?:item|file))?\b/i.test(prompt)) return null
  const target = selectedTargets[0]
  if (!target || (target.type !== 'GOAL' && target.type !== 'TASK' && target.type !== 'SUBTASK')) {
    throw new Error('I need a selected goal, task, or subtask to create a checklist item. Pick the parent first, then ask again.')
  }
  const title = extractCreatedTitle(prompt, 'CHECKLIST')
  if (!title) throw new Error('Name the checklist item in the request, for example "Create a checklist item called Venue insurance review."')
  return {
    id: newActionId(),
    type: 'CREATE_CHECKLIST_ITEM',
    config: { parentId: target.id, title },
  }
}

const parseActions = (
  prompt: string,
  selectedTargets: AiOperationTargetRef[],
  actorId: string,
): { then: AutomationAction[]; warnings: string[] } => {
  const lower = prompt.toLowerCase()
  const warnings: string[] = []
  const then: AutomationAction[] = []
  const conditionOnlyCompletion = /\bwhen (it|this|the item|the task|the checklist) is completed\b/.test(lower)

  if (/\bnotif(y|ies|ied)\b|\balert\b|\bremind\b|\bping\b/.test(lower)) {
    pushAction(then, createNotifyAction(prompt, actorId))
  }
  if ((/\bsend\b|\bpost\b/.test(lower) && /\bchat\b|\bchannel\b|\bmessage\b/.test(lower)) || /\bslack\b/.test(lower)) {
    pushAction(then, createSendChatAction(prompt, warnings))
  }
  if (/\blogs?\b|\blogging\b|\bledger\b|\baudit\b/.test(lower)) {
    pushAction(then, createLedgerAction(prompt))
  }
  if (/\bassign(?:s|ed)?\b/.test(lower) && /\bto\b/.test(lower)) {
    pushAction(then, createAssignAction(prompt, selectedTargets, actorId))
  }
  const shouldCompleteTarget = !conditionOnlyCompletion && (/\bmark\b.*\bcomplete\b|\bcomplete this\b|\bcomplete the\b|\bclose\b/.test(lower))
  if (shouldCompleteTarget) {
    pushAction(then, createCompleteAction(selectedTargets))
  }
  const statusAction = conditionOnlyCompletion || shouldCompleteTarget ? null : createStatusAction(prompt, selectedTargets)
  if (statusAction) pushAction(then, statusAction)
  const checklistCreationAction = createChecklistCreationAction(prompt, selectedTargets)
  if (checklistCreationAction) pushAction(then, checklistCreationAction)
  const nodeCreationAction = createNodeCreationAction(prompt, selectedTargets, warnings)
  if (nodeCreationAction) pushAction(then, nodeCreationAction)

  if (then.length === 0) {
    throw new Error('I could not identify the automation action yet. Ask for a clear outcome such as notify, send a chat message, assign the selected item to me, log it, mark it complete, or create a goal/task/subtask/checklist item.')
  }
  return { then, warnings }
}

export const compileAutomationActionBundleFromPrompt = (
  input: CompileAutomationDraftInput,
): CompiledAutomationActionBundle => {
  const prompt = input.prompt.trim()
  if (!prompt) throw new Error('Add the request first.')
  if (promptSoundsLikeFutureAutomation(prompt)) {
    throw new Error('This request describes a future or recurring trigger. Use Create automation for that path instead of Do it now.')
  }
  const actions = parseActions(prompt, input.selectedTargets, input.actorId)
  const targetLabel = input.selectedTargets[0]?.title?.trim() ?? null
  const summaryParts = [
    'Executed from AI Magic Execute.',
    `Original request: ${trimmedSentence(prompt, 180)}`,
    targetLabel ? `Selected target: ${targetLabel}.` : '',
    actions.warnings.length ? `Review notes: ${actions.warnings.join(' ')}` : '',
  ].filter(Boolean)
  return {
    name: titleCase(trimmedSentence(prompt, 60)),
    description: summaryParts.join('\n\n'),
    actions: actions.then,
    warnings: actions.warnings,
  }
}

export const compileAutomationDraftFromPrompt = (
  input: CompileAutomationDraftInput,
): CompiledAutomationDraft => {
  const prompt = input.prompt.trim()
  if (!prompt) throw new Error('Add the automation request first.')
  const trigger = parseTrigger(prompt, input.selectedTargets)
  const actions = parseActions(prompt, input.selectedTargets, input.actorId)
  const warnings = [...trigger.warnings, ...actions.warnings]
  const targetLabel = input.selectedTargets[0]?.title?.trim() ?? null
  const summaryParts = [
    'Created from AI Magic Execute.',
    `Original request: ${trimmedSentence(prompt, 180)}`,
    targetLabel ? `Selected target: ${targetLabel}.` : '',
    warnings.length ? `Review notes: ${warnings.join(' ')}` : '',
  ].filter(Boolean)
  return {
    name: titleCase(trimmedSentence(prompt, 60)),
    description: summaryParts.join('\n\n'),
    triggers: [trigger.trigger],
    then: actions.then,
    warnings,
  }
}
